import type {FC} from 'react';

import {ArticleListAllContainer} from '@/modules/article/sanity/article-list-all-container';
import {ArticleListLatestContainer} from '@/modules/article/sanity/article-list-latest-container';
import {ArticleListSelectionContainer} from '@/modules/article/sanity/article-list-selection-container';
import {ContactFormSectionContainer} from '@/modules/contact-form/sanity/contact-form-section-container';
import {CompaniesListAllContainer} from '@/modules/finkn/companies/content-blocks/sanity/companies-list-all';
import {CompaniesListLinksContainer} from '@/modules/finkn/companies/content-blocks/sanity/companies-list-links';
import {TableOfContents} from '@/modules/finkn/components/table-of-contents';
import {BannerContainer} from '@/modules/finkn/content-blocks/sanity/banner';
import {CardLinkListSelectionContainer} from '@/modules/finkn/content-blocks/sanity/card-link-list-selection';
import {CollapsibleContentContainer} from '@/modules/finkn/content-blocks/sanity/collapsible-content';
import {CookieListContainer} from '@/modules/finkn/content-blocks/sanity/cookie-list';
import {FactsListContainer} from '@/modules/finkn/content-blocks/sanity/facts-list';
import {FaqListAllContainer} from '@/modules/finkn/faq/content-blocks/sanity/faq-list-all';
import {FaqListAreaContainer} from '@/modules/finkn/faq/content-blocks/sanity/faq-list-area';
import {FaqListSelectionContainer} from '@/modules/finkn/faq/content-blocks/sanity/faq-list-selection';
import {AreaListAllContainer} from '@/modules/finkn/field-of-area/content-blocks/sanity/area-list-all';
import {FieldOfAreaListAllContainer} from '@/modules/finkn/field-of-area/content-blocks/sanity/field-of-area-list-all';
import {GuidebookListAllContainer} from '@/modules/finkn/field-of-area/content-blocks/sanity/guidebook-list-all';
import {NemndListAllContainer} from '@/modules/finkn/nemnd/sanity/nemnd-list-all';
import {LinkListSelectionContainer} from '@/modules/page/sanity/content-blocks/link-list-selection-container';
import {TextContainer} from '@/modules/page/sanity/content-blocks/text-container';
import {PersonListSelectionContainer} from '@/modules/person/sanity/person-list-selection-container';

import type {ContentBlocksFragment} from './query';

type Props = {
	content: ContentBlocksFragment;
	hasToc?: boolean;
};

const ContentBlocks: FC<Props> = ({content, hasToc = false}) => {
	const sections = content?.map((section) => {
		switch (section._type) {
			case 'textSection':
				return (
					<TextContainer
						key={section._key}
						data={section}
						anchorHeadings={hasToc}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'linkListSelection':
				return (
					<LinkListSelectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'articleListSelection':
				return (
					<ArticleListSelectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'articleListAll':
				return (
					<ArticleListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'articleListLatest':
				return (
					<ArticleListLatestContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'personListSelection':
				return (
					<PersonListSelectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeading={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'contactFormSection':
				return (
					<ContactFormSectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'fieldOfAreaListAll':
				return (
					<FieldOfAreaListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'guidebook-list-all':
				return (
					<GuidebookListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'area-list-all':
				return (
					<AreaListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'faq-list-all':
				return (
					<FaqListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'faq-list-selection':
				return (
					<FaqListSelectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'faq-list-area':
				return (
					<FaqListAreaContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'nemnd-list-all':
				return (
					<NemndListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'collapsible-content':
				return (
					<CollapsibleContentContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'card-link-list-selection':
				return (
					<CardLinkListSelectionContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'banner':
				return (
					<BannerContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'facts-list':
				return (
					<FactsListContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'companies-list-all':
				return (
					<CompaniesListAllContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'companies-list-links':
				return (
					<CompaniesListLinksContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						anchorHeadings={hasToc}
						insideToc={hasToc}
					/>
				);
			case 'cookie-list':
				return (
					<CookieListContainer
						key={section._key}
						data={section}
						fullWidth={hasToc}
						insideToc={hasToc}
					/>
				);
			default:
				return null;
		}
	});

	return <>{hasToc ? <TableOfContents>{sections}</TableOfContents> : <>{sections}</>}</>;
};

export default ContentBlocks;
